import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Briefcase, CheckCircle2, ChevronRight, Clock, Loader2, X,
} from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { api } from '@/lib/api';
import { bizDateTime } from '@/lib/dates';

/**
 * TalentStart UAT remediation — READ-ONLY employee onboarding view.
 * Employees have NO operational stage actions (FINDING 2): they only see the
 * route, stages, statuses, progress, dates, hours and messages. The welcome
 * banner uses the persisted completion timestamp and is dismissible with
 * server persistence and a server-computed 7-day expiry (FINDING 3/4).
 */

const STATUS_TONE = {
  not_started: 'bg-muted/40 text-muted-foreground border-muted',
  in_progress: 'bg-warning/15 text-warning border-warning/30',
  completed: 'bg-success/15 text-success border-success/30',
  overdue: 'bg-destructive/15 text-destructive border-destructive/30',
};

const STAGE_TONE = {
  not_started: 'bg-muted/40 text-muted-foreground',
  in_progress: 'bg-warning/15 text-warning',
  completed: 'bg-success/15 text-success',
};

export default function MeOnboardingCard() {
  const { t, i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  const [asg, setAsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await clientApi.onboardingMyOwn();
      setAsg(d);
    } catch {
      setAsg(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card className="p-4 flex items-center gap-2 text-muted-foreground" data-testid="me-onboarding-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {isES ? 'Cargando onboarding…' : 'Loading onboarding…'}
      </Card>
    );
  }
  if (!asg) return null;

  const dismissWelcome = async () => {
    try {
      await api.post(`/me/onboarding/${asg.id}/dismiss-welcome`);
      setAsg((prev) => ({ ...prev, welcome_alert: { ...prev.welcome_alert, visible: false } }));
    } catch { /* noop */ }
  };

  const current = asg.stages[asg.current_stage_idx];

  return (
    <Card className="p-5 shadow-card border-primary/30 bg-primary/5" data-testid="me-onboarding-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-primary font-semibold">
            <Briefcase className="inline h-3 w-3 mr-1" />
            {t('talentstart.me.routeTitle', { defaultValue: isES ? 'Tu ruta de onboarding' : 'Your onboarding route' })}
          </p>
          <p className="text-lg font-bold mt-0.5 truncate">{asg.name_snapshot || asg.position_snapshot}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={STATUS_TONE[asg.status]} data-testid="me-onboarding-status">
            {t(`talentstart.status.${asg.status}`, { defaultValue: asg.status })}
          </Badge>
          <span className="text-2xl font-bold tabular-nums text-primary" data-testid="me-onboarding-progress">{asg.progress_pct}%</span>
        </div>
      </div>

      {/* Stage stepper — read-only */}
      <ol className="flex flex-wrap items-center gap-1 mb-3" data-testid="me-onboarding-stepper">
        {asg.stages.map((s) => (
          <li key={s.idx} className="flex items-center gap-1">
            <div
              data-testid={`me-onboarding-stage-${s.idx}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${STAGE_TONE[s.status]} ${s.idx === asg.current_stage_idx ? 'ring-2 ring-primary' : ''}`}
            >
              <span className="font-bold tabular-nums">{s.idx + 1}</span>
              <span className="truncate max-w-[9rem]">{s.name}</span>
              {s.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
            </div>
            {s.idx < asg.stages.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          </li>
        ))}
      </ol>

      {/* Current stage — informational only, no operational actions */}
      {current && asg.status !== 'completed' && (
        <div className="rounded-md border bg-card p-3 flex items-start gap-3" data-testid="me-onboarding-current">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {t('talentstart.me.currentStage', { defaultValue: isES ? 'Etapa actual: ' : 'Current stage: ' })}{current.name}
            </p>
            <p className="text-sm text-muted-foreground">
              <Clock className="inline h-3 w-3 mr-1" />
              {current.estimated_hours}h ·{' '}
              {current.department_name || t('talentstart.me.noDept', { defaultValue: isES ? 'Sin departamento' : 'No department' })}
              {current.responsible_name && <> · {current.responsible_name}</>}
            </p>
            {current.description && <p className="text-sm mt-1">{current.description}</p>}
            <p className="text-xs text-muted-foreground mt-2 italic" data-testid="me-onboarding-info-note">
              {t('talentstart.me.hrManaged', {
                defaultValue: isES
                  ? 'Recursos Humanos coordina y registra el avance de tu onboarding.'
                  : 'Human Resources coordinates and records your onboarding progress.',
              })}
            </p>
          </div>
        </div>
      )}

      {/* Completion / welcome alert — persisted timestamp, dismissible, 7-day expiry */}
      {asg.status === 'completed' && asg.welcome_alert?.visible && (
        <div className="rounded-md border bg-success/10 text-success p-3 text-sm font-medium flex items-start gap-2" data-testid="me-onboarding-completed">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex-1">
            {t('talentstart.me.completedOn', {
              date: bizDateTime(asg.welcome_alert.completed_at, isES ? 'es' : 'en'),
              defaultValue: isES
                ? 'Onboarding completado el {{date}}. ¡Bienvenido al equipo!'
                : 'Onboarding completed on {{date}}. Welcome to the team!',
            })}
          </span>
          <button type="button" onClick={dismissWelcome} aria-label={isES ? 'Descartar' : 'Dismiss'}
                  className="opacity-60 hover:opacity-100" data-testid="me-onboarding-welcome-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {asg.status === 'completed' && !asg.welcome_alert?.visible && (
        <p className="text-sm text-muted-foreground" data-testid="me-onboarding-completed-info">
          {t('talentstart.me.completedInfo', {
            date: bizDateTime(asg.welcome_alert?.completed_at || asg.completed_at, isES ? 'es' : 'en'),
            defaultValue: isES ? 'Onboarding completado el {{date}}.' : 'Onboarding completed on {{date}}.',
          })}
        </p>
      )}
    </Card>
  );
}
